import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import Alert from "../Alert";
import Button from "../Button";
import ButtonPrimary from "../ButtonPrimary";
import FormGroup from "../FormGroup";
import Label from "../Label";
import Modal from "../Modal";
import PasswordInput from "../PasswordInput";
import { IDeleteAccountProps } from "./types";
import { ModalHeader } from "../Modal/ModalHeader";
import { ModalContent } from "../Modal/ModalContent";
import { ModalFooter } from "../Modal/ModalFooter";

import "./styles.scss";

export const DeleteAccountModal: FC<IDeleteAccountProps> = ({
  children,
  className,
  isOpen,
  errors,
  onDismiss,
  onSubmit,
  isLoading,
  user,
  ...props
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(true);

  const handleChangePassword = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setIsPasswordValid(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    onSubmit(password);
  };

  useEffect(() => {
    if (errors) {
      if (errors.password) {
        setPassword("");
        setIsPasswordValid(false);
      }
    }
  }, [errors]);

  const errorList = errors ? Object.values(errors) : [];

  return (
    <Modal
      className={classNames("ams-delete-account-modal", className)}
      isOpen={isOpen}
      {...props}
    >
      <ModalHeader>{t("accountDeletion")}</ModalHeader>
      <ModalContent>
        {!!errorList.length && (
          <div className="ams-delete-account-modal__validation-alert">
            <Alert
              className="ams-delete-account-modal__error-alert"
              variant="error"
              content={
                <ul className="ams-delete-account-modal__error-list">
                  {errorList.map((errorData) => {
                    return <li key={errorData}>{t(`error.${errorData}`)}</li>;
                  })}
                </ul>
              }
            />
          </div>
        )}
        <div className="ams-change-password-modal__password-alert">
          <Alert variant="warning">{t("accountDeletionIsNotReversible")}</Alert>
        </div>
        <form className="delete-account-form" onSubmit={handleSubmit}>
          <FormGroup>
            <Label>{t("confirmPassword")}</Label>
            <PasswordInput
              value={password}
              error={!isPasswordValid}
              onChange={handleChangePassword}
              id="password-input"
            />
          </FormGroup>
        </form>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onDismiss} className="ams-modal__action">
          {t("cancel")}
        </Button>
        <ButtonPrimary
          onClick={handleSubmit}
          className="ams-modal__action"
          disabled={!password || !isPasswordValid}
          isLoading={isLoading}
        >
          {t("deleteAccount")}
        </ButtonPrimary>
      </ModalFooter>
    </Modal>
  );
};
